import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUpDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const MultiSelectColumnFilter = ({
    columnId,
    label,
    options = [],
    selectedValues = [],
    onChange,
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const [isPositioned, setIsPositioned] = useState(false);

    // Calculate coordinates for the portal dropdown
    const calculateCoords = useCallback(() => {
        if (!dropdownRef.current) return null;
        const rect = dropdownRef.current.getBoundingClientRect();

        // If rect is all zeroes, the element might be hidden or detached
        if (rect.top === 0 && rect.left === 0 && rect.width === 0) {
            return null;
        }

        let left = rect.left + rect.width / 2;

        // Constrain to viewport (dropdown is 16rem = 256px wide)
        const dropdownWidth = 256;
        const halfWidth = dropdownWidth / 2;
        const padding = 12;

        // Ensure we don't go off screen horizontally
        if (left - halfWidth < padding) {
            left = halfWidth + padding;
        } else if (left + halfWidth > window.innerWidth - padding) {
            left = window.innerWidth - halfWidth - padding;
        }

        return {
            top: rect.bottom + 6, // 6px gap below the icon
            left: left
        };
    }, []);

    // Update position function
    const updatePosition = useCallback(() => {
        if (isOpen) {
            const newCoords = calculateCoords();
            if (newCoords) {
                setCoords(newCoords);
                setIsPositioned(true);
            }
        }
    }, [isOpen, calculateCoords]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !event.target.closest('.portal-dropdown-content')) {
                setIsOpen(false);
                setSearchTerm('');
                setIsPositioned(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            // Handle escape key
            const handleEsc = (e) => {
                if (e.key === 'Escape') {
                    setIsOpen(false);
                }
            };
            document.addEventListener('keydown', handleEsc);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                document.removeEventListener('keydown', handleEsc);
            };
        }
    }, [isOpen]);

    // Close on scroll or resize to prevent drifting
    useEffect(() => {
        if (isOpen) {
            // Initial positioning
            updatePosition();

            const handleClose = (e) => {
                // Do NOT close if the scroll happened inside the dropdown menu itself
                if (e.target && typeof e.target.closest === 'function' && e.target.closest('.portal-dropdown-content')) {
                    return;
                }
                setIsOpen(false);
                setSearchTerm('');
                setIsPositioned(false);
            };

            window.addEventListener('scroll', handleClose, { capture: true, passive: true });
            window.addEventListener('resize', handleClose);

            return () => {
                window.removeEventListener('scroll', handleClose, { capture: true });
                window.removeEventListener('resize', handleClose);
            };
        }
    }, [isOpen, updatePosition]);

    // Focus search input when dropdown opens and position is ready
    useEffect(() => {
        if (isOpen && isPositioned && searchInputRef.current) {
            const timer = setTimeout(() => {
                searchInputRef.current?.focus();
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [isOpen, isPositioned]);

    const [tempSelectedValues, setTempSelectedValues] = useState([]);

    // Initialize tempSelectedValues ONLY when dropdown opens.
    // We avoid resetting it on every render when selectedValues prop changes while open,
    // as parent re-renders (like periodic background fetches) would wipe out the user's pending selections.
    useEffect(() => {
        if (isOpen) {
            setTempSelectedValues(Array.isArray(selectedValues) ? [...selectedValues] : []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        return options.filter(option =>
            option.label?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [options, searchTerm]);

    const handleToggle = (optionValue) => {
        setTempSelectedValues(prev => {
            const currentSelected = Array.isArray(prev) ? prev : [];
            return currentSelected.includes(optionValue)
                ? currentSelected.filter(v => v !== optionValue)
                : [...currentSelected, optionValue];
        });
    };

    const handleClear = (e) => {
        e.stopPropagation();
        if (!isTempActive && !isActive) return;
        setTempSelectedValues([]);
        onChange([]);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleApply = (e) => {
        e.stopPropagation();
        onChange(tempSelectedValues);
        setIsOpen(false);
    };

    const isActive = Array.isArray(selectedValues) && selectedValues.length > 0;
    const isTempActive = Array.isArray(tempSelectedValues) && tempSelectedValues.length > 0;

    const dropdownContent = (
        <div
            className={`portal-dropdown-content fixed z-[9999] w-64 bg-white border border-gray-200 rounded-lg shadow-2xl overflow-hidden ${isPositioned ? 'animate-in fade-in zoom-in-95 duration-100' : ''}`}
            style={{
                top: `${coords.top}px`,
                left: `${coords.left}px`,
                transform: 'translateX(-50%)',
                visibility: isPositioned ? 'visible' : 'hidden',
                opacity: isPositioned ? 1 : 0,
                pointerEvents: isPositioned ? 'auto' : 'none'
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Header */}
            <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">{label}</span>
            </div>

            {/* Search */}
            <div className="p-2 border-b border-gray-100">
                <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={`Search ${label}...`}
                        className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            </div>

            {/* Options List */}
            <div className="max-h-60 overflow-y-auto py-1 custom-scrollbar">
                {filteredOptions.length === 0 ? (
                    <div className="px-3 py-4 text-xs text-gray-500 text-center">
                        No {label.toLowerCase()} found
                    </div>
                ) : (
                    filteredOptions.map((option) => (
                        <label
                            key={option.value}
                            className="flex items-center px-3 py-1.5 hover:bg-blue-50 cursor-pointer transition-colors group"
                        >
                            <div className="relative flex items-center h-4">
                                <input
                                    type="checkbox"
                                    checked={Array.isArray(tempSelectedValues) && tempSelectedValues.includes(option.value)}
                                    onChange={() => handleToggle(option.value)}
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                />
                            </div>
                            <span className={`ml-3 text-xs ${Array.isArray(tempSelectedValues) && tempSelectedValues.includes(option.value) ? 'text-blue-800 font-medium' : 'text-gray-700'}`}>
                                {option.label}
                            </span>
                        </label>
                    ))
                )}
            </div>

            {/* Footer / Summary */}
            <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                <div className="flex flex-col">
                    <button
                        onClick={handleClear}
                        disabled={!isTempActive && !isActive}
                        className={`text-[10px] font-bold uppercase text-left mb-1 ${
                            isTempActive || isActive
                                ? 'text-blue-600 hover:text-blue-800 cursor-pointer'
                                : 'text-gray-400 cursor-not-allowed'
                        }`}
                        title={isTempActive || isActive ? 'Clear filter' : 'No filters applied'}
                    >
                        Clear
                    </button>
                    <span className="text-[10px] text-gray-500">
                        {Array.isArray(tempSelectedValues) ? tempSelectedValues.length : 0} selected
                    </span>
                </div>
                <button
                    onClick={handleApply}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-bold hover:bg-blue-700 transition-colors"
                >
                    Apply
                </button>
            </div>
        </div>
    );

    return (
        <div ref={dropdownRef} className="relative inline-block ml-1">
            {/* Filter Toggle Icon */}
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) {
                        const nextState = !isOpen;
                        if (nextState) {
                            const newCoords = calculateCoords();
                            if (newCoords) {
                                setCoords(newCoords);
                                setIsPositioned(true);
                            }
                        } else {
                            setIsPositioned(false);
                            setSearchTerm('');
                        }
                        setIsOpen(nextState);
                    }
                }}
                disabled={disabled}
                className={`
                    p-1 rounded transition-colors
                    ${isActive ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' : 'text-gray-400 hover:bg-gray-100'}
                    ${isOpen ? 'bg-gray-200 text-gray-700' : ''}
                `}
                title={`Filter by ${label}`}
            >
                <ChevronUpDownIcon className="h-4 w-4" />
            </button>

            {/* Dropdown Menu - Rendered via Portal at body level to avoid clipping */}
            {isOpen && createPortal(dropdownContent, document.body)}
        </div>
    );
};

export default MultiSelectColumnFilter;
